import React from "react";
import { Link } from "react-router-dom";

const About = () => {
  // Common theme colors
  const colors = {
    primary: "#0c416a",
    primaryDark: "#082f4d",
    accent: "#f0582b",
    accentHover: "#d84c26",
    textDark: "#2c3e50",
    textMuted: "#5a6b7c",
    bgLight: "#f8fafc",
    white: "#ffffff",
    border: "#e2e8f0",
  };

  return (
    <section 
      className="about-page bg-[#f8fafc] text-[#2c3e50] min-h-screen" 
    >
      {/* Hero Section */}
      <div
        className="about-hero text-white text-center py-16 sm:py-24 px-4 sm:px-6"
        style={{
          background: `linear-gradient(135deg, ${colors.primary}, ${colors.primaryDark})`,
        }}
      >
        <div className="max-w-3xl mx-auto">
          <span className="uppercase tracking-widest text-xs sm:text-sm font-semibold text-[#f0582b] block mb-3">
            Our Story & Mission
          </span>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 leading-tight">
            About NRI Local Buddy
          </h1>
          <p className="text-base sm:text-lg md:text-xl font-light opacity-90 leading-relaxed max-w-2xl mx-auto">
            Bridging miles with care, compassion, and commitment — because love has no distance.
          </p>
        </div>
      </div>

      {/* Main Content Container */}
      <div className="container max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 md:py-20">
        
        {/* Core Narrative Grid */}
        <div className="mb-10 sm:mb-16">
          <div className="bg-white p-6 sm:p-8 md:p-10 rounded-2xl shadow-sm border border-[#e2e8f0]">
            <p className="text-base sm:text-lg leading-relaxed text-[#2c3e50] mb-4 sm:mb-6">
              Living abroad opens doors to incredible opportunities — successful careers, global exposure, and a bright future for your children.
              Yet, deep within, there’s a quiet ache — the guilt of being far from your parents and elders in India,
              the longing to care for them as they once cared for you.
            </p>
            <p className="text-base sm:text-lg leading-relaxed text-[#2c3e50] mb-4 sm:mb-6">
              You may have thought of seeking help from friends or relatives, but hesitation and awkwardness often hold you back.
              And even when you do ask, the “favor” sometimes comes with an unspoken price — perhaps an iPhone, a gadget,
              or a return favor. The truth is, genuine care should never come with conditions.
            </p>
            <p className="text-base sm:text-lg leading-relaxed text-[#2c3e50] m-0">
              That’s where <strong className="text-[#f0582b] font-semibold">NRI Local Buddy</strong> steps in —
              a heartfelt solution for every NRI who wishes to stay connected to their family’s well-being back home.
              For less than the cost of those unspoken compensations, you gain something priceless:
              professional, dedicated, and compassionate care for your loved ones.
            </p>
          </div>
        </div>

        {/* Founder Section */}
        <div
          className="about-founder bg-white rounded-2xl p-6 sm:p-8 md:p-10 mb-10 sm:mb-16 shadow-sm border border-[#e2e8f0] relative overflow-hidden"
        >
          {/* Subtle decorative background graphic */}
          <div className="absolute -top-5 left-4 text-[8rem] sm:text-[12rem] text-[#0c416a]/5 font-serif select-none leading-none pointer-events-none">
            “
          </div>

          <div className="relative z-10">
            <span className="uppercase tracking-wider text-xs font-semibold text-[#f0582b] block mb-2">
              Founder's Vision
            </span>
            <h2 className="text-2xl sm:text-3xl font-bold text-[#0c416a] mb-4 sm:mb-6">
              Meet Srinivas GRK
            </h2>
            
            <div className="border-l-4 border-[#f0582b] pl-4 sm:pl-6 my-4 sm:my-6">
              <p className="text-base sm:text-lg leading-relaxed text-[#2c3e50] italic mb-3">
                "The journey from a thriving career in the USA back to the warmth of my mother's embrace in India inspired a vision grounded in love, responsibility, and connection."
              </p>
              <span className="font-semibold text-[#0c416a] text-sm sm:text-base">— Srinivas GRK, Founder</span>
            </div>

            <p className="text-sm sm:text-base leading-relaxed text-[#5a6b7c] mt-4">
              The heart and soul behind NRI Local Buddy, Srinivas knows firsthand the emotions of distance and duty.
              His story is more than an inspiration; it’s a mission — to ensure no NRI ever feels helpless or distant
              when it comes to the well-being of their family back home.
            </p>
          </div>
        </div>

        {/* Mission / Promise Section */}
        <div
          className="about-promise bg-[#0c416a] text-white rounded-2xl p-6 sm:p-10 mb-10 sm:mb-16 shadow-lg text-center"
        >
          <h3 className="text-2xl sm:text-3xl font-semibold mb-4 text-white">Our Promise</h3>
          <p className="text-base sm:text-lg leading-relaxed max-w-3xl mx-auto opacity-95">
            NRI Local Buddy isn’t just a service — it’s a heartfelt pledge. A promise to turn{" "}
            <em className="text-[#ffd275] not-italic font-semibold">distance into connection</em>,{" "}
            <em className="text-[#ffd275] not-italic font-semibold">guilt into action</em>, and{" "}
            <em className="text-[#ffd275] not-italic font-semibold">longing into love</em>.
            With us by your side, your loved ones will always feel cared for, cherished, and valued —
            just as if you were there yourself.
          </p>
        </div>

        {/* Closing CTA */}
        <div className="about-closing text-center py-6">
          <h2 className="text-2xl sm:text-3xl font-bold text-[#0c416a]">Join Our Journey</h2>
          <p className="max-w-2xl mx-auto my-4 sm:my-6 text-[#5a6b7c] text-base sm:text-lg leading-relaxed">
            Together, let’s bridge hearts across borders. Trust{" "}
            <strong className="text-[#f0582b] font-semibold">NRI Local Buddy</strong> to be your presence back home —
            because care, after all, is the purest form of love.
          </p>
          <Link
            to="/contact"
            className="inline-block bg-[#f0582b] hover:bg-[#d84c26] text-white px-8 py-3.5 sm:py-4 rounded-full font-semibold text-base transition-all duration-200 shadow-md hover:-translate-y-0.5"
          >
            Get in Touch
          </Link>
        </div>

      </div>
    </section>
  );
};

export default About;